import { NgInject } from '@df/ng/ng-inject';
import { IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import flatten from 'lodash-es/flatten';
import uniq from 'lodash-es/uniq';
import { LocalPage } from 'projects/app-default/src/app/catalog/page/local/local-page';
import { NBlogCategoryPageType } from '../../page/category-view/cms-blog-page-category-view.component';
import { ListingFilter } from '@df/catalog-ui/listing/listing';
import { Filter } from '@df/catalog-ui/listing/filter/filter';
import { NBlogPostPageType } from '../../page/post-view/cms-blog-post.namespace';

@ListingFilter({
    filterType: 'blogTag'
})
export class CmsBlogContentTagFilter extends Filter<LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>, number[]> {
    /**
     * Initial selection
     */
    protected override _selection: number[] = [];

    /**
     * Category filter alway has matches
     */
    hasAnyMatches = false;

    /**
     * Page resource
     */
    @NgInject(ICmsPageResource)
    private pageResource!: ICmsPageResource;

    /**
     * Tags (pages)
     */
    options: LocalPage<IPageDataV2<NBlogCategoryPageType.ITypeData>>[] = [];

    /**
     * Retrive pages (blog categories) on init
     */
    protected override onInit() {
        const optionsIds = uniq(flatten(this.listing.getSourceModels().map(i => i.getBlogPostCategoryIds())));
        this.options = (this.pageResource.getAllExisting(optionsIds) as LocalPage<IPageDataV2<NBlogCategoryPageType.ITypeData>>[]) || [];
        this.hasAnyMatches = !!this.options.length;
        super.onInit();
    }

    /**
     * Applies filter to models collection
     */
    applyFilter(collection: LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>[]): LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>[] {
        if (!this.hasSelection) {
            return collection;
        }
        // filter collection
        return collection.filter(model => !!model.getBlogPostCategoryIds()?.find(i => this.selection?.includes(i)));
    }

    getState(): number[] {
        return this._selection;
    }

    setState(data: string | number | (string | number)[]) {
        if (!data) {
            return;
        }
        if (Array.isArray(data)) {
            this.selection = <number[]>data;
        } else {
            if (typeof data === 'string') {
                this.selection = data.split(',').map(i => parseInt(i, 10));
            } else if (typeof data === 'number') {
                this.selection = [data];
            }
        }
    }

    reset() {
        this.selection = [];
        this.next();
    }

    select(optionId: number, unselectOther = true) {
        if (unselectOther) {
            this.selection = [optionId];
        } else {
            if (!this.isSelected(optionId)) {
                this.selection?.push(optionId);
            }
        }
        this.next();
    }

    unselect(optionId: number) {
        this.selection = this.selection?.filter(i => i !== optionId);
        this.next();
    }

    isSelected(optionId: number) {
        return this.selection?.includes(optionId) ?? false;
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._selection.length;
    }

    /**
     * Idenfing function for options repeater
     */
    optionTrackByFn(index: number, option: any) {
        return option.id;
    }
}
